import { useQuery } from "@apollo/react-hooks";
import gql from "graphql-tag";
import React from "react";
import Moment from "react-moment";
import { useLocation, useParams } from "react-router-dom";
import Permissions from "../../configs/permissions";
import useItemCounter from "../../hooks/itemCounter";
import usePermissions from "../../hooks/permissions";
import {
  renderOrderPaymentStatus,
  renderOrderState,
  renderOrderStatus,
} from "../TilloBrandListApprovalPage";
import { REPORTS_USER_CINEMA_ORDERS_MODEL_NAME } from "./../../configs/constants";
import { cinemaSocietyOrders } from "./../../graphql/queries/reportQueries";
import useBaseNavigation from "./../../hooks/baseNavigation";
import useTypeFilters from "./../../hooks/typeFilters";
import { TEditButton } from "./../../moose-ui/Controls";
import {
  Pagination,
  TCol,
  TContent,
  THCol,
  THeading,
  TRow,
  TableActions,
  TableContainer,
  TableSelectedActions,
  TableView,
} from "./../../moose-ui/DataTable";
import {
  BlockContainer,
  Col,
  PageContainer,
  Row,
} from "./../../moose-ui/Layout";
import PageSpinner from "./../../moose-ui/PageSpinner";
import Search from "./../../moose-ui/Search";
import { Heading, ItemsNotFound } from "./../../moose-ui/Typography";
import TypeFilters from "./../../moose-ui/filters/TypeFilters";

const CINEMA_ORDERS = gql(cinemaSocietyOrders);

const CinemaSocietyOrders = () => {
  const { protectPage } = usePermissions();
  protectPage(Permissions.Query.CinemaOrdersReport);
  const { page, limit, orderBy, orderHow, search } = useParams();
  const { search: queryString } = useLocation();
  const { data, loading, error } = useQuery(CINEMA_ORDERS, {
    variables: {
      page,
      limit,
      orderBy,
      orderHow,
      search,
      queryString,
    },
    fetchPolicy: "cache-and-network",
  });

  const { itemsTotal } = useItemCounter(data, {
    searchKey: "cinemaOrders",
  });

  const { nextPage, prevPage, applySearch, applyFilters, resetFilters, goTo } =
    useBaseNavigation(REPORTS_USER_CINEMA_ORDERS_MODEL_NAME, {
      showingItemsCount: itemsTotal,
    });

  const {
    data: typeFilters,
    selectedFilters,
    hasActiveFilters,
    updateFilter: onUpdateFilter,
    updateDatesFilter: onUpdateDatesFilter,
    resetFilters: onResetFilters,
    removeFilterValue: onRemoveFilterValue,
    applyFilters: onApplyFilters,
    isReady: typeFiltersReady,
  } = useTypeFilters({
    filtersFor: "cinemaOrders",
    onApply: applyFilters,
    onReset: resetFilters,
    queryString: queryString,
    fetchPolicy: "no-cache",
  });

  if (error) return <div>Error</div>;
  if (!data || !typeFiltersReady) return <PageSpinner />;

  return (
    <PageContainer>
      <BlockContainer>
        {loading && (
          <Heading text="Report. User Cinema Society Orders. Loading..." />
        )}
        {!loading && <Heading text="Report. User Cinema Society Orders" />}
        <Row>
          <Col width={12}>
            <TypeFilters
              data={typeFilters}
              onApply={onApplyFilters}
              onReset={onResetFilters}
              onRemoveValue={onRemoveFilterValue}
              hasActiveFilters={hasActiveFilters}
              onUpdate={onUpdateFilter}
              onUpdateDates={onUpdateDatesFilter}
              selectedFilters={selectedFilters}
              filterBaseTitle="Dates & Filters"
            />
          </Col>
        </Row>
        <Row>
          <Col width={9}></Col>
          <Col width={3}>
            <Search onApplySearch={applySearch} />
          </Col>
        </Row>

        {data && data.cinemaOrders && data.cinemaOrders.length > 0 && (
          <TableContainer>
            <TableView>
              <THeading>
                <TRow>
                  <THCol name="order_id" value="Order Id" />
                  <THCol name="transaction_id" value="Transaction Id" />
                  <THCol name="chain_name" value="Chain" />
                  <THCol name="venue_name" value="Venue" />
                  <THCol name="email" value="User Email" />
                  <THCol name="client" value="Account Name" />
                  <THCol name="amount" value="Total Amount, £" />
                  <THCol name="final_amount" value="Final Amount, £" />
                  <THCol name="payment_status" value="Payment Status" />
                  <THCol name="order_status" value="Order Status" />
                  <THCol name="state" value="State" />
                  <THCol name="created_at" value="Order Date" />
                  <THCol />
                </TRow>
              </THeading>
              <TContent>
                {data.cinemaOrders.map((order, idx) => (
                  <TRow key={idx}>
                    <TCol value={order.id} />
                    <TCol value={order.transaction_id} />
                    <TCol value={order.chain_name} />
                    <TCol value={order.venue_name} />
                    <TCol value={order.user.email} />
                    <TCol value={order.client.account_name} />
                    <TCol>{order.amount}</TCol>
                    <TCol>{order.final_amount}</TCol>
                    <TCol>
                      {renderOrderPaymentStatus(order.bank_payment_status)}
                    </TCol>
                    <TCol>{renderOrderStatus(order.order_status)}</TCol>
                    <TCol>{renderOrderState(order.state)}</TCol>
                    <TCol>
                      <Moment format="LL" date={order.created_at} />
                    </TCol>
                    <TCol>
                      <TEditButton
                        onClick={() => {
                          goTo(
                            `/${REPORTS_USER_CINEMA_ORDERS_MODEL_NAME}/${order.id}`
                          );
                        }}
                        label="View"
                      />
                    </TCol>
                  </TRow>
                ))}
              </TContent>
            </TableView>
            <TableActions>
              <TableSelectedActions></TableSelectedActions>
              <Pagination onPrev={prevPage} onNext={nextPage} />
            </TableActions>
          </TableContainer>
        )}
        {data && data.orders && data.orders.length === 0 && (
          <ItemsNotFound text="No user cinema society orders found by current criteria" />
        )}
      </BlockContainer>
    </PageContainer>
  );
};

export default CinemaSocietyOrders;
